import { useTranslation } from "react-i18next";
import type { FaqItem } from "@/pages/help/faqData";

interface FaqBatchPublishModalProps {
  items: FaqItem[];
  saving: boolean;
  onClose: () => void;
  onConfirm: () => void;
}

export default function FaqBatchPublishModal({ items, saving, onClose, onConfirm }: FaqBatchPublishModalProps) {
  const { t } = useTranslation();

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 px-4 py-6">
      <div className="flex max-h-full w-full max-w-lg flex-col overflow-hidden rounded-lg border border-background-200 bg-background-50">
        <div className="flex items-start justify-between gap-4 border-b border-background-200 px-5 py-4">
          <div className="flex items-start gap-3">
            <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-md bg-primary-100 text-primary-700">
              <i className="ri-send-plane-line text-lg" />
            </span>
            <div>
              <h3 className="font-heading text-base font-semibold text-foreground-950">
                {t("helpFeedback.batchPublishTitle")}
              </h3>
              <p className="mt-1 text-xs leading-relaxed text-foreground-500">
                {t("helpFeedback.batchPublishDesc", { count: items.length })}
              </p>
            </div>
          </div>
          <button
            type="button"
            onClick={onClose}
            aria-label={t("helpFeedback.contentCancel")}
            className="flex h-7 w-7 shrink-0 cursor-pointer items-center justify-center rounded-md text-foreground-500 transition-colors hover:bg-background-100 hover:text-foreground-900"
          >
            <i className="ri-close-line" />
          </button>
        </div>

        <div className="max-h-72 flex-1 overflow-y-auto px-5 py-4">
          <ul className="space-y-1.5">
            {items.map((item) => (
              <li
                key={item.qKey}
                className="flex items-center gap-2 rounded-md border border-background-200 bg-background-100 px-3 py-2"
              >
                <span className="flex h-4 w-4 shrink-0 items-center justify-center text-secondary-700">
                  <i className="ri-draft-line" />
                </span>
                <span className="min-w-0 truncate text-xs text-foreground-800">{t(item.qKey)}</span>
              </li>
            ))}
          </ul>
        </div>

        <div className="flex items-center justify-end gap-2 border-t border-background-200 px-5 py-4">
          <button
            type="button"
            onClick={onClose}
            disabled={saving}
            className="cursor-pointer whitespace-nowrap rounded-md border border-background-300 bg-background-50 px-4 py-2 text-sm text-foreground-800 transition-colors hover:bg-background-100 disabled:opacity-60"
          >
            {t("helpFeedback.contentCancel")}
          </button>
          <button
            type="button"
            onClick={onConfirm}
            disabled={saving}
            className="inline-flex cursor-pointer items-center gap-1.5 whitespace-nowrap rounded-md bg-primary-500 px-4 py-2 text-sm font-medium text-background-50 transition-colors hover:bg-primary-600 disabled:cursor-not-allowed disabled:opacity-60"
          >
            <span className="flex h-4 w-4 items-center justify-center">
              <i className={saving ? "ri-loader-4-line animate-spin" : "ri-send-plane-line"} />
            </span>
            {saving ? t("helpFeedback.batchPublishing") : t("helpFeedback.batchPublishConfirm")}
          </button>
        </div>
      </div>
    </div>
  );
}